import { useState, useEffect, useRef } from 'react';
import { resolverAudioCaptura } from '@/shared/lib/fonteReproducao';
import type { YoutubeSlideHandle } from '../components/YoutubeSlide';
import type { CaptureSlideHandle } from '../components/CaptureSlide';
import type { FonteReproducao, PerfilAudio } from '@/shared/types';

export interface UsePlayerMediaSourceConfig {
  fonte_reproducao: FonteReproducao;
  audio_captura:    'playlist' | 'captura';
  ganho_captura?:   number;
  perfil_audio?:    PerfilAudio;
}

export interface UsePlayerMediaSourceParams {
  playerConfig:     UsePlayerMediaSourceConfig;
  ytSlideRef:       React.RefObject<YoutubeSlideHandle | null>;
  ytLiveRef:        React.RefObject<YoutubeSlideHandle | null>;
  captureSlideRef:  React.RefObject<CaptureSlideHandle | null>;
  playerPlayingRef: React.RefObject<boolean>;
  playerMutedRef:   React.RefObject<boolean>;
  playerVolumeRef:  React.RefObject<number>;
  broadcastEstadoPlayerRef: React.RefObject<() => void>;
  transmissaoVideoId: string | null;
}

export interface UsePlayerMediaSourceReturn {
  origemMidia:       FonteReproducao;
  audioCaptura:      'playlist' | 'captura';
  ganhoCaptura:      number;
  perfilAudio:       PerfilAudio;
  ytUnlocked:        boolean;
  janelaStreamAtivo: boolean;
  setOrigem(origem: FonteReproducao, audio: 'playlist' | 'captura'): void;
  setGanho(g: number): void;
  setPerfil(p: PerfilAudio): void;
  setJanelaStreamAtivo: React.Dispatch<React.SetStateAction<boolean>>;
  setYtUnlocked: React.Dispatch<React.SetStateAction<boolean>>;
}

/** Origem de mídia (playlist/captura/captura_janela/transmissão), DSP de
 *  áudio (ganho/perfil) das capturas, e sincronização de play/pause/mute
 *  entre os YoutubeSlides off-screen conforme a fonte ativa. */
export function usePlayerMediaSource({
  playerConfig, ytSlideRef, ytLiveRef, captureSlideRef,
  playerPlayingRef, playerMutedRef, playerVolumeRef,
  broadcastEstadoPlayerRef, transmissaoVideoId,
}: UsePlayerMediaSourceParams): UsePlayerMediaSourceReturn {
  const [origemMidia,  setOrigemMidiaState]  = useState<FonteReproducao>('playlist');
  const [audioCaptura, setAudioCapturaState] = useState<'playlist' | 'captura'>('playlist');

  useEffect(() => {
    setOrigemMidiaState(playerConfig.fonte_reproducao);
    setAudioCapturaState(resolverAudioCaptura(playerConfig.fonte_reproducao, playerConfig.audio_captura));
  }, [playerConfig.fonte_reproducao, playerConfig.audio_captura]);

  // Corrige estado legado: audio_captura='captura' com fonte playlist
  useEffect(() => {
    if (origemMidia === 'playlist' && audioCaptura !== 'playlist') {
      setAudioCapturaState('playlist');
    }
  }, [origemMidia, audioCaptura]);

  const [ganhoCaptura, setGanhoCapturaState] = useState(playerConfig.ganho_captura ?? 100);
  useEffect(() => { setGanhoCapturaState(playerConfig.ganho_captura ?? 100); }, [playerConfig.ganho_captura]);
  useEffect(() => { captureSlideRef.current?.setGanho(ganhoCaptura); }, [ganhoCaptura, captureSlideRef]);

  const [perfilAudio, setPerfilAudioState] = useState<PerfilAudio>(playerConfig.perfil_audio ?? 'natural');
  useEffect(() => { setPerfilAudioState(playerConfig.perfil_audio ?? 'natural'); }, [playerConfig.perfil_audio]);
  useEffect(() => { captureSlideRef.current?.setPerfil(perfilAudio); }, [perfilAudio, captureSlideRef]);

  const [ytUnlocked, setYtUnlocked] = useState(false);
  const [janelaStreamAtivo, setJanelaStreamAtivo] = useState(false);
  const unlockTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const precisaUnlock =
      (origemMidia !== 'playlist' && audioCaptura === 'playlist')
      || origemMidia === 'transmissao';
    if (!precisaUnlock || ytUnlocked) return;
    setYtUnlocked(true);
    const t = setTimeout(() => {
      ytSlideRef.current?.play();
      if (origemMidia === 'transmissao') ytLiveRef.current?.play();
    }, 500);
    unlockTimerRef.current = t;
    return () => { clearTimeout(t); };
    // ytUnlocked sai das deps de propósito: o setState daqui re-rodaria o
    // effect e o cleanup abortaria o play() de 500 ms. Unmount/troca de
    // origem ainda cancela — é o mesmo useEffect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [origemMidia, audioCaptura, ytSlideRef, ytLiveRef]);

  useEffect(() => {
    const pausarPlaylistPorAudioCaptura = () => {
      ytSlideRef.current?.mute();
      ytSlideRef.current?.pause();
      if (playerPlayingRef.current) {
        playerPlayingRef.current = false;
        broadcastEstadoPlayerRef.current();
      }
    };

    if (origemMidia === 'playlist') {
      ytLiveRef.current?.mute();
      ytLiveRef.current?.pause();
      if (playerPlayingRef.current) ytSlideRef.current?.play();
      else ytSlideRef.current?.pause();
      if (!playerMutedRef.current) {
        ytSlideRef.current?.unMute();
        ytSlideRef.current?.setVolume(playerVolumeRef.current);
      } else {
        ytSlideRef.current?.mute();
      }
      return;
    }

    if (origemMidia === 'transmissao') {
      if (audioCaptura === 'captura') {
        pausarPlaylistPorAudioCaptura();
        ytLiveRef.current?.play();
        if (!playerMutedRef.current) ytLiveRef.current?.unMute();
        else ytLiveRef.current?.mute();
      } else {
        ytLiveRef.current?.mute();
        if (playerPlayingRef.current) ytLiveRef.current?.play();
        if (playerPlayingRef.current) ytSlideRef.current?.play();
        if (!playerMutedRef.current) ytSlideRef.current?.unMute();
        else ytSlideRef.current?.mute();
      }
      return;
    }

    ytLiveRef.current?.mute();
    ytLiveRef.current?.pause();

    if (origemMidia === 'captura_janela') {
      if (!janelaStreamAtivo) {
        ytSlideRef.current?.mute();
        ytSlideRef.current?.pause();
        return;
      }
      if (audioCaptura === 'captura') {
        pausarPlaylistPorAudioCaptura();
        return;
      }
      if (playerPlayingRef.current) ytSlideRef.current?.play();
      if (!playerMutedRef.current) ytSlideRef.current?.unMute();
      return;
    }

    if (audioCaptura === 'captura') {
      pausarPlaylistPorAudioCaptura();
    } else {
      if (playerPlayingRef.current) ytSlideRef.current?.play();
      if (!playerMutedRef.current) ytSlideRef.current?.unMute();
    }
  }, [audioCaptura, origemMidia, janelaStreamAtivo, ytSlideRef, ytLiveRef, playerPlayingRef, playerMutedRef, playerVolumeRef, broadcastEstadoPlayerRef]);

  useEffect(() => {
    if (origemMidia !== 'transmissao' || !transmissaoVideoId || !ytUnlocked) return;
    ytLiveRef.current?.play();
  }, [origemMidia, transmissaoVideoId, ytUnlocked, ytLiveRef]);

  useEffect(() => {
    if (origemMidia !== 'captura_janela') setJanelaStreamAtivo(false);
  }, [origemMidia]);

  function setOrigem(origem: FonteReproducao, audio: 'playlist' | 'captura'): void {
    const audioEfetivo = resolverAudioCaptura(origem, audio);
    setOrigemMidiaState(origem);
    setAudioCapturaState(audioEfetivo);
  }

  function setGanho(g: number): void {
    setGanhoCapturaState(g);
    captureSlideRef.current?.setGanho(g);
  }

  function setPerfil(p: PerfilAudio): void {
    setPerfilAudioState(p);
    captureSlideRef.current?.setPerfil(p);
  }

  return {
    origemMidia, audioCaptura, ganhoCaptura, perfilAudio, ytUnlocked, janelaStreamAtivo,
    setOrigem, setGanho, setPerfil, setJanelaStreamAtivo, setYtUnlocked,
  };
}
